import {
  useGetDisburseRecordByBatchQuery,
  type EncashmentRecord,
} from '@/app/features/employee/emp-payroll';
import CustomTable from '@/components/CustomTable';
import { FILTER_KEYS } from '@/constants/keys.constants';
import { useAppFilters } from '@/hooks';
import DashboardLayout from '@/layouts/DashboardLayout';
import { formatDate } from '@/utils';
import type { TableProps } from 'antd';
import { useParams } from 'react-router';
import PayrollPageHeader from '../shared/PayrollPageHeader';
import TotalAmount from '../shared/TotalAmount';
import { PAYROLL_PATH } from '../shared/payroll.constants';
import { formatBDT } from '../shared/payroll.helpers';
import EncashmentToolbar from './EncashmentToolbar';
import { employeeColumns, leaveColumns } from './encashment.helpers';

/** The records of an encashment batch that have already been disbursed. */
export default function BatchDisbursedRecords() {
  const { id = '' } = useParams();
  const {
    page,
    limit,
    skip,
    filters,
    keyword,
    handleSearch,
    handleFilter,
    handlePagination,
  } = useAppFilters<{ office_id?: string }>(
    `${FILTER_KEYS.PAYROLL_LEAVE}-${id}-disbursed`,
  );

  const { data, isLoading, isFetching } = useGetDisburseRecordByBatchQuery(
    {
      id,
      args: {
        params: { skip, limit, search: keyword, office_id: filters.office_id },
      },
    },
    { skip: !id },
  );
  const batch = data?.data;
  const records = batch?.records || [];

  const [idColumn, ...otherEmployeeColumns] = employeeColumns || [];
  const columns: TableProps<EncashmentRecord>['columns'] = [
    idColumn,
    {
      title: <span className="whitespace-nowrap">Generated On</span>,
      dataIndex: 'disbursed_at_date',
      render: (val: string) => (val ? formatDate(val) : 'n/a'),
    },
    ...otherEmployeeColumns,
    ...(leaveColumns(records) || []),
    {
      title: <span className="whitespace-nowrap">Total Amount</span>,
      dataIndex: 'total_amount',
      align: 'center',
      render: (val: string) => formatBDT(val || null),
    },
    {
      title: 'Note',
      dataIndex: 'note',
      align: 'center',
      render: (val?: string | null) => val ?? 'n/a',
    },
  ];

  return (
    <DashboardLayout title="Employee">
      <PayrollPageHeader
        crumbs={[
          { title: 'Leave Encashment', path: PAYROLL_PATH },
          { title: 'Encashment List' },
        ]}
      >
        <EncashmentToolbar
          batch={batch}
          searchPlaceholder="Search Employee ID"
          onSearch={handleSearch}
          officeId={filters.office_id}
          onOfficeChange={(value) => handleFilter('office_id', value)}
        />
      </PayrollPageHeader>

      <div className="mt-6">
        <CustomTable<EncashmentRecord>
          page={page}
          limit={limit}
          hideRowSelect
          columns={columns}
          dataSource={records}
          onPaginate={handlePagination}
          customTitle={batch?.batch_name || 'Encashment Batch'}
          tableRootClassName="!bg-transparent !border-none !p-0"
          loading={isLoading || isFetching}
          total={batch?.records_meta?.total || 0}
          filterSection={<TotalAmount value={batch?.total_amount} />}
        />
      </div>
    </DashboardLayout>
  );
}
